/**
 * Shared constants for call-off-related API calls.
 */

/**
 * Call-off status enum values representing the lifecycle states.
 *
 * State flow:
 * - draft → requested → confirmed → confirmed_locked
 * - At various points: can go to declined, cancelled, or deleted
 */
export const CALL_OFF_STATUS = [
  'draft', // Being prepared, not yet sent to supplier
  'requested', // Forwarded to supplier via E-mail, awaiting response
  'confirmed', // Both parties agree on the call-off terms
  'supplier_change_requested', // Supplier confirmed with modifications, awaiting buyer review
  'recipient_change_requested', // Recipient requested changes, awaiting supplier review
  'declined', // Supplier or recipient declined the call-off (terminal before confirmation)
  'confirmed_locked', // Final mutual agreement reached, no more changes allowed (terminal)
  'cancel_requested', // One party requested cancellation, awaiting other party approval
  'cancelled', // Cancellation agreed by both parties (terminal)
  'deleted', // Soft delete, only from DRAFT state (terminal)
] as const;

/**
 * Source of the information about the delivery site.
 */
export const DELIVERY_SITE_SOURCE = [
  'linked_site', // User selected a delivery site that is part of our master data
  'free_text', // ??? User entered a delivery site name manually
  'manual', // ???
] as const;

/**
 * Actions that trigger state transitions via the buyer respond API.
 * These are the actions sent to POST /magic_link/respond endpoint.
 */
export const BUYER_RESPOND_ACTION = [
  // From DRAFT (send to supplier)
  'send_request',

  // From REQUESTED (direct cancel before supplier responds)
  'cancel',

  // From CONFIRMED (buyer actions after mutual agreement)
  'request_changes',
  'lock_confirmation',
  'request_cancel',

  // From SUPPLIER_CHANGE_REQUESTED (buyer responds to supplier's change request)
  'accept_changes',
  'counter_propose',
  'decline_changes',

  // From CANCEL_REQUESTED (buyer responds to supplier's cancel request)
  'accept_cancel',
  'deny_cancel',
] as const;

/**
 * All actions that can be performed on a call-off.
 * This is returned by GET /magic_link/actions endpoint.
 *
 * Available actions depend on the current call-off status.
 */
export const BUYER_ACTION = [
  // UI-only actions (not state transitions)
  'resend_magic_link', // Resend magic link email (requested only)
  'edit', // Edit the call-off (draft only)
  'delete', // Delete the call-off (draft only)
  'duplicate', // Create a copy (always available)

  // State transition actions
  ...BUYER_RESPOND_ACTION,
] as const;

/**
 * Actions that trigger state transitions via the supplier respond API.
 * These are the actions sent to POST /magic_link/:calloff_id/respond endpoint (public).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Off-Magic-Link-(Public)/operation/supplier_respond_calloffs_magic_link__calloff_id__respond_post
 */
export const SUPPLIER_RESPOND_ACTION = [
  // From REQUESTED (supplier initial response)
  'confirm', // Accept the call-off as is → CONFIRMED
  'confirm_with_changes', // Accept with modifications → SUPPLIER_CHANGE_REQUESTED
  'decline', // Decline the call-off → DECLINED

  // From CONFIRMED (supplier actions after mutual agreement)
  'request_changes', // Request changes to the call-off → RECIPIENT_CHANGE_REQUESTED
  'request_cancel', // Request cancellation → CANCEL_REQUESTED

  // From RECIPIENT_CHANGE_REQUESTED (supplier responds to buyer's change request)
  'accept_changes', // Accept buyer's requested changes → CONFIRMED
  'counter_propose', // Counter-propose with different changes → SUPPLIER_CHANGE_REQUESTED

  // From CANCEL_REQUESTED (supplier responds to buyer's cancel request)
  'accept_cancel', // Accept the cancellation request → CANCELLED
  'deny_cancel', // Deny the cancellation request → CONFIRMED
] as const;

/**
 * All actions that can be performed on a call-off by the supplier.
 * This is returned by GET /magic_link/:calloff_id endpoint as part of the call-off data.
 *
 * Available actions depend on the current call-off status.
 */
export const SUPPLIER_ACTION = [...SUPPLIER_RESPOND_ACTION] as const;

/**
 * Valid state transitions for the call-off state machine.
 * Maps each status to its valid next states.
 *
 * Terminal states (no outgoing transitions): declined, confirmed_locked, cancelled, deleted
 */
export const CALL_OFF_TRANSITIONS = {
  // CANCEL_REQUESTED: One party wants to cancel, awaiting other party approval
  cancel_requested: [
    'cancelled', // Other party accepts cancellation
    'confirmed', // Cancel request denied - back to confirmed
  ],

  // CANCELLED: Terminal state - cancellation agreed by both parties
  cancelled: [],

  // CONFIRMED: Both parties agree. Can negotiate further, lock, or request cancellation.
  confirmed: [
    'supplier_change_requested', // Supplier requests change (after confirmation)
    'recipient_change_requested', // Buyer requests changes (after confirmation)
    'confirmed_locked', // Lock the confirmation - end negotiation
    'cancel_requested', // Request cancellation (needs other party approval)
  ],

  // CONFIRMED_LOCKED: Terminal state - final mutual agreement, no more changes allowed
  confirmed_locked: [],

  // DECLINED: Terminal state - supplier or recipient declined before confirmation
  declined: [],

  // DELETED: Terminal state - no transitions allowed
  deleted: [],

  // DRAFT: Can send to supplier (REQUESTED) or delete. Only state that can delete.
  draft: ['requested', 'deleted'],

  // RECIPIENT_CHANGE_REQUESTED: Buyer requested changes. Supplier reviews.
  recipient_change_requested: [
    'confirmed', // Supplier accepts the requested changes
    'supplier_change_requested', // Supplier counter-proposes
    'declined', // Supplier declines the requested changes
  ],

  // REQUESTED: Awaiting supplier response. Supplier can confirm, confirm with changes, or decline.
  // Customer can cancel before supplier confirms (direct cancellation, no approval needed).
  requested: [
    'confirmed',
    'supplier_change_requested',
    'declined',
    'cancelled',
  ],

  // SUPPLIER_CHANGE_REQUESTED: Supplier confirmed with modifications. Buyer reviews.
  supplier_change_requested: [
    'confirmed', // Buyer accepts supplier's changes
    'recipient_change_requested', // Buyer counter-proposes
    'declined', // Buyer declines supplier's changes
  ],
} as const satisfies Record<
  (typeof CALL_OFF_STATUS)[number],
  readonly (typeof CALL_OFF_STATUS)[number][]
>;

/**
 * Terminal states that have no outgoing transitions.
 */
export const CALL_OFF_TERMINAL_STATES = [
  'declined',
  'confirmed_locked',
  'cancelled',
  'deleted',
] as const satisfies readonly (typeof CALL_OFF_STATUS)[number][];
